import { Response } from '../../../../../_metronic/helpers'
import { EmpresaList } from '../../empresa/empresas-list/core/_models'
import { EspecialidadeList } from '../../especialidades/especialidades-list/core/_models'

/**
 * Tipos espelhando as views do backend em
 * `br.com.lauduzone.auth.models.views.agenda` (`EscalaList`, `EscalaViewPessoa`,
 * `EscalaAgendaViewPessoa`, `EscalaViewEmpresa`, `EscalaAgendaViewEmpresa`,
 * `FeriadoList`/`FeriadoView`).
 *
 * Datas são `LocalDate` puras (`AAAA-MM-DD`) e horários são `LocalTime`
 * (`HH:mm` ou `HH:mm:ss`, conforme o Jackson omita ou não os segundos) — ambos
 * chegam **sem fuso**. Formate com `formatDateToPtBr`/`formatHorario`, nunca com
 * `new Date(str)` (ver `lauduzone-frontend/CLAUDE.md`, seção de datas).
 */

export enum TipoEscala {
  Disponibilidade = 'DISPONIBILIDADE',
  Indisponibilidade = 'INDISPONIBILIDADE',
}

export enum DiaSemana {
  Segunda = 'SEGUNDA',
  Terca = 'TERCA',
  Quarta = 'QUARTA',
  Quinta = 'QUINTA',
  Sexta = 'SEXTA',
  Sabado = 'SABADO',
  Domingo = 'DOMINGO',
}

export enum TipoFeriado {
  Nacional = 'NACIONAL',
  Estadual = 'ESTADUAL',
  Municipal = 'MUNICIPAL',
  Institucional = 'INSTITUCIONAL',
}

/** Tipo de atendimento (`TipoAtendimento` no backend) — `PA`, `ASO`, `SC`, `TK`, `CA`, `IC`. */
export type TipoAtendimentoEscala = string

export type PessoaEscalaList = {
  id: number
  nome: string
  nomeSocial?: string
  primeiroNome?: string
  sobreNome?: string
}

/** `EscalaEmpresaList` / `EscalaAgendaEmpresaList` — o vínculo empresa × especialidade. */
export type EscalaVinculoList = {
  id: number
  empresa?: EmpresaList
  especialidade?: EspecialidadeList
}

/** `EscalaList` — linha da listagem paginada de `GET /escalas` e `GET /escalas/empresa/{empresa}`. */
export type EscalaList = {
  id: number
  tipo: TipoEscala
  dataInicio: string
  dataTermino?: string
  horarioInicio?: string
  horarioTermino?: string
  dataInicioFormatada?: string
  dataTerminoFormatada?: string
}

/** `EscalaViewPessoa` — detalhe de `GET /escalas/{id}`. */
export type EscalaViewPessoa = EscalaList & {
  diasSemana?: DiaSemana[]
  tiposAtendimento?: TipoAtendimentoEscala[]
  vinculos?: EscalaVinculoList[]
  empresas?: EmpresaList[]
  especialidades?: EspecialidadeList[]
}

/** `EscalaViewEmpresa` — detalhe de `GET /escalas/empresa/{empresa}/{id}`. */
export type EscalaViewEmpresa = EscalaList & {
  pessoa?: PessoaEscalaList
  diasSemana?: DiaSemana[]
  tiposAtendimento?: TipoAtendimentoEscala[]
  vinculos?: EscalaVinculoList[]
}

/** `EscalaAgendaViewPessoa` — um dia da agenda pessoal. */
export type EscalaAgendaViewPessoa = {
  id: number
  tipo: TipoEscala
  data: string
  horarioInicio?: string
  horarioTermino?: string
  dataFormatada?: string
  tiposAtendimento?: TipoAtendimentoEscala[]
  vinculos?: EscalaVinculoList[]
  empresas?: EmpresaList[]
  especialidades?: EspecialidadeList[]
  disponivel: boolean
  indisponivel: boolean
  diaInteiro: boolean
}

/** `EscalaAgendaViewEmpresa` — um dia da agenda vista pela empresa. Traz `pessoa`. */
export type EscalaAgendaViewEmpresa = {
  id: number
  pessoa?: PessoaEscalaList
  tipo: TipoEscala
  data: string
  horarioInicio?: string
  horarioTermino?: string
  dataFormatada?: string
  tiposAtendimento?: TipoAtendimentoEscala[]
  /**
   * Vínculos (empresa × especialidade) da agenda. **Vazio é sinal semântico**:
   * é uma indisponibilidade global do profissional (registrada sem empresa), que
   * a empresa enxerga mas não gerencia — ver `naoGerenciavelPelaEmpresa`.
   */
  vinculos?: EscalaVinculoList[]
  disponivel: boolean
  indisponivel: boolean
  diaInteiro: boolean
}

/** `GET /escalas/agenda` → `Map<LocalDate, List<EscalaAgendaViewPessoa>>`. */
export type AgendaPessoaResponse = Record<string, EscalaAgendaViewPessoa[]>

/**
 * `GET /escalas/empresa/agenda/{empresa}` → `Map<PessoaList, Map<LocalDate, List<...>>>`.
 *
 * ⚠️ A chave externa é um `PessoaList` serializado pelo Jackson como chave de
 * mapa (`toString()` do objeto) — **é opaca e não deve ser interpretada**. O
 * profissional vem dentro de cada item, em `EscalaAgendaViewEmpresa.pessoa`.
 */
export type AgendaEmpresaResponse = Record<string, Record<string, EscalaAgendaViewEmpresa[]>>

/** Corpo de `POST /escalas` (`EscalaDtoCadastroPessoa`). */
export type EscalaDtoCadastroPessoa = {
  tipo: TipoEscala
  dataInicio: string
  dataTermino?: string | null
  horarioInicio?: string | null
  horarioTermino?: string | null
  diasSemana?: DiaSemana[] | null
  /** Opcional quando `tipo = INDISPONIBILIDADE`: vazio vale para todas as empresas com vínculo. */
  empresas?: number[] | null
  especialidades?: number[] | null
  tiposAtendimento?: TipoAtendimentoEscala[] | null
}

/** Corpo de `POST /escalas/empresa/{empresa}` (`EscalaDtoCadastroEmpresa`). */
export type EscalaDtoCadastroEmpresa = Omit<EscalaDtoCadastroPessoa, 'empresas'> & {
  pessoa: number
}

/** Corpo de `PUT /escalas/agenda` (`EscalaAgendaDtoAlteracaoPessoa`) — edita **um dia**. */
export type EscalaAgendaDtoAlteracaoPessoa = {
  id: number
  tipo: TipoEscala
  data: string
  horarioInicio?: string | null
  horarioTermino?: string | null
  empresas?: number[] | null
  especialidades?: number[] | null
  tiposAtendimento?: TipoAtendimentoEscala[] | null
}

/** Corpo de `PUT /escalas/empresa/agenda/{empresa}` (`EscalaAgendaDtoAlteracaoEmpresa`). */
export type EscalaAgendaDtoAlteracaoEmpresa = Omit<EscalaAgendaDtoAlteracaoPessoa, 'empresas'>

export type FeriadoList = {
  id: number
  nome: string
  data: string
  dataFormatada?: string
  anual?: boolean
  tipo: TipoFeriado
}

export type FeriadoView = FeriadoList & {
  empresa?: EmpresaList
}

/** Corpo de `POST /feriados/empresa/{id}` (`FeriadoDtoCadastro`). */
export type FeriadoDtoCadastro = {
  tipo: TipoFeriado
  nome: string
  data: string
  /** Junto com `data`, forma um período (ex.: carnaval). */
  intervalo?: string | null
  anual: boolean
}

/** Corpo de `PUT /feriados/{id}` (`FeriadoDtoAlteracao`). */
export type FeriadoDtoAlteracao = {
  tipo: TipoFeriado
  nome: string
  data: string
  anual: boolean
}

export type EscalasQueryResponse = Response<Array<EscalaList>>
export type FeriadosQueryResponse = Response<Array<FeriadoList>>
